/**
 * skuColours — the IGCom catalogue's colour vocabulary, in one place.
 *
 * IT IS READ BY TWO MODULES AND OWNED BY NEITHER. The payroll payout names a
 * garment's colour from it, and the cutting floor's W.O. Log and production
 * tags now do too — "MSGRN it should be Moss Green" (Mikko, 2026-09-23),
 * because a code on a sticker is a code the cutter has to translate in their
 * head. It lived in `subcontractors/garmentName.ts` until the second reader
 * arrived; it is here for the same reason `production-grid/parentCategories`
 * is where it is, and garmentName re-exports it so nothing in payroll moved.
 *
 * EXTRACTED, NOT GUESSED. Every pair came off the real IGCom SKU catalog and
 * was kept only where the code is a plausible abbreviation of the name — a
 * table of guesses would put a wrong word on a document somebody signs and on
 * paper a cutter works from, and nothing about looking at it would say which
 * words were guesses.
 *
 * AN UNKNOWN CODE PRINTS ITSELF, never a blank.
 *
 * Pure and I/O-free.
 */

const up = (v: string | null | undefined): string => String(v ?? "").trim().toUpperCase();

/**
 * Colour code -> name, from the catalog. Combinations that are just their
 * parts joined ("ARGRN-MLGRN") are NOT here — `colourName` composes those.
 */
export const COLOUR_NAMES: Readonly<Record<string, string>> = {
  "ADBLU-BLK": "Admiral Blue - Black", AMVLT: "Amethyst Violet", APPCH: "Apricot Peach",
  AQBLU: "Aqua Blue", ARGRN: "Army Green", ASGRY: "Ash Grey", AZBLU: "Azure Blue",
  BBYPNK: "Baby Pink", BGE: "Beige", BLK: "Black", BLPNK: "Blush Pink",
  "BRN-KHK": "Chocolate Brown - Khaki", CDPNK: "Candy Pink", CHBRN: "Choco Brown",
  CHGRY: "Charcoal Grey", CML: "Caramel", "CNPNK-WHT": "Carnation Pink - White",
  COBRN: "Coffee Brown", COGRN: "Cobalt Green", CRGRN: "Carribean Green", DNBLU: "Denim Blue",
  EMGRN: "Emerald Green", FEGRN: "Fern Green", FRGRN: "Forest Green", JDGRN: "Jade Green",
  KHK: "Khaki", LEGRY: "Lead Grey", LVVLT: "Lavender Violet", MDBLU: "Midnight Blue",
  "MDBLU-KLGRN": "Midnight Blue - Kelly Green", MLGRN: "Military Green",
  MMBRN: "Mocha Mousse Brown", MOC: "Mocha", "MOC-BRN": "Mocha - Brown", MRN: "Maroon",
  MSGRN: "Moss Green", MSTRD: "Mustard Yellow", MVRS: "Mauve Rose", NDPNK: "Nude Pink",
  NVBLU: "Navy Blue", OXBLU: "Oxford Blue", "PCFZZ-CHGRY": "Peach Fuzz - Charcoal Grey",
  PCHFZZ: "Peach Fuzz", PLM: "Plum Violet", PRBLU: "Persian Blue", PRPLMV: "Purple Mauve",
  PRPLMVE: "Purple Mauve", PWBLU: "Powder Blue", RED: "Ruby Red", RSGRN: "Russian Green",
  RST: "Rust Orange", RYBLU: "Royal Blue", SGGRN: "Sage Green", SLGRY: "Silver Grey",
  SPBLU: "Sapphire Blue", STBLU: "Steel Blue", STKHK: "Skintone Khaki", TERST: "Terra Rust",
  TLBLU: "Teal Blue", TLGRN: "Teal Green", VLMVE: "Velvet Mauve", VLT: "Violet",
  "VLT-LLC": "Violet - Lilac", VOGRN: "Vogue Green", WHT: "White", WLT: "Walnut",
  "WLT-BRN": "Walnut - Brown",
};

/** A colour code in words, composing a combination from its parts. */
export function colourName(code: string | null | undefined): string {
  const c = up(code);
  if (!c) return "";
  const known = COLOUR_NAMES[c];
  if (known) return known;
  const parts = c.split("-").filter(Boolean);
  if (parts.length > 1) {
    const named = parts.map(p => COLOUR_NAMES[p]);
    if (named.every(Boolean)) return named.join(" - ");
  }
  return c;   // never blank: a code beats nothing on a document somebody signs
}

